'use client'

import Link from 'next/link'
import { useState } from 'react'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { env } from '~/env'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) {
      setError('Please enter your email address')
      return
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError('Please enter a valid email address')
      return
    }

    try {
      setLoading(true)
      setError(null)
      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      })
      if (!res.ok) {
        // Validation error from the API. The endpoint deliberately doesn't
        // distinguish unknown-email from sent — both come back as 200.
        const body = await res.json().catch(() => null)
        setError(body?.message ?? 'Could not request a reset link. Please try again.')
        setLoading(false)
        return
      }
      setSubmitted(true)
    } catch (err) {
      console.error('Password reset request failed:', err)
      setError('Could not reach the server. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="flex flex-col items-center relative overflow-y-auto"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: '4rem',
        bottom: 0,
        width: '100vw',
        // #1567: the page "Forgot password" opens; it was missed when the
        // reset page moved to the login page's floral background.
        backgroundImage: 'url(/assets/login-background-botanical.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        margin: 0,
        padding: 0,
      }}
    >
      {/* #1567: same dimming as the login and reset pages over this image. */}
      <div aria-hidden className="absolute inset-0 bg-background/75" />
      <div className="relative z-10 mx-auto max-w-2xl w-full px-4 my-auto py-8">
        <div className="text-center mb-6">
          <h1
            className="text-5xl font-bold text-gray-900 mb-2 font-serif"
          >
            Reset your password
          </h1>
          <p className="text-sm text-gray-600 mb-5">
            We&apos;ll email you a link to choose a new one.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">
                If an account exists for that email, a reset link has been
                sent. Check your inbox (and spam folder) for the email.
              </div>
              <Link
                href="/login?from=loginpage"
                className="block text-center text-sm text-[#DE866D] hover:underline"
              >
                Back to log in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              {error && (
                <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (error) setError(null)
                  }}
                  className="w-full"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full bg-[#DE866D] hover:bg-[#C97257] disabled:bg-[#DE866D] disabled:opacity-70 text-white h-11"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>
              <div className="text-center">
                <Link
                  href="/login?from=loginpage"
                  className="text-sm text-[#DE866D] hover:underline"
                >
                  Back to log in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
